import { VStack, HStack, Button, Text, Box, Switch, Field } from '@chakra-ui/react';
import type { GuidedListSpec } from '../../../types/guidedList'
import {
  SCORE_KNOBS, SCORE_BANDS, AUDIENCE_DEMOGRAPHICS, isNonAdjacent, GUIDED_ISSUES,
  issueCompileCaption, type IssueKey, type IssueDirection,
} from '../../../types/guidedList'
import { FLAG_KEYS, FLAG_BADGES, FLAG_DESCRIPTIONS } from '../../../types/analyticsFlags'
import { AudienceGroup } from '../audience/AudienceGroup'
import { BandChipRow } from '../audience/BandChipRow'
import { MultiSelectChipRow } from '../audience/MultiSelectChipRow'
import { UnionOnly } from '../../scoping/UnionOnly'

interface StepProps { spec: GuidedListSpec; patch: (p: Partial<GuidedListSpec>) => void }

export function AudienceStep({ spec, patch }: StepProps) {
  const arr = (k: keyof GuidedListSpec) => (spec[k] as string[])
  const smartOptions = FLAG_KEYS.map(k => ({ value: k, label: FLAG_BADGES[k].label, tip: FLAG_DESCRIPTIONS[k] }))

  return (
    <VStack align="stretch" gap={3}>
      <AudienceGroup title="Issue alignment" defaultOpen>
        <VStack align="stretch" gap={3}>
          {GUIDED_ISSUES.map(gi => <IssueAlignmentRow key={gi.key} spec={spec} patch={patch} issue={gi} />)}
        </VStack>
      </AudienceGroup>

      <AudienceGroup title="Lean & propensity">
        <VStack align="stretch" gap={4}>
          {SCORE_KNOBS.map(k => {
            const order = SCORE_BANDS[k.bands].map(b => b.key)
            return <BandChipRow key={k.specKey} label={k.label} tip={k.tip}
              options={SCORE_BANDS[k.bands]} value={arr(k.specKey as keyof GuidedListSpec)}
              onChange={v => patch({ [k.specKey]: v } as Partial<GuidedListSpec>)}
              nonAdjacentHint={isNonAdjacent(arr(k.specKey as keyof GuidedListSpec), order)} />
          })}
        </VStack>
      </AudienceGroup>

      <AudienceGroup title="Coalition & demographics">
        <VStack align="stretch" gap={4}>
          {AUDIENCE_DEMOGRAPHICS.map(d => (
            <MultiSelectChipRow key={d.specKey} label={d.label}
              options={d.options.map(o => ({ value: o, label: o }))}
              value={arr(d.specKey as keyof GuidedListSpec)}
              onChange={v => patch({ [d.specKey]: v } as Partial<GuidedListSpec>)} />
          ))}
        </VStack>
      </AudienceGroup>

      <AudienceGroup title="Smart targets">
        <MultiSelectChipRow label="Include anyone who is any of" options={smartOptions}
          value={spec.smartFlags} onChange={v => patch({ smartFlags: v })} />
      </AudienceGroup>

      <AudienceGroup title="Reachability & hygiene">
        <VStack align="stretch" gap={3}>
          <ToggleRow id="regdem" label="Registered Democrats only" checked={spec.registeredDemOnly} onChange={v => patch({ registeredDemOnly: v })} />
          <ToggleRow id="regactive" label="Registration = Active" checked={spec.registrationActiveOnly} onChange={v => patch({ registrationActiveOnly: v })} />
          <ToggleRow id="pabs" label="Permanent absentee / vote-by-mail" checked={spec.permanentAbsenteeOnly} onChange={v => patch({ permanentAbsenteeOnly: v })} />
          <ToggleRow id="celldnc" label="Exclude cell DNC" checked={spec.excludeCellDnc} onChange={v => patch({ excludeCellDnc: v })} />
          <ToggleRow id="landdnc" label="Exclude land DNC" checked={spec.excludeLandDnc} onChange={v => patch({ excludeLandDnc: v })} />
          <ToggleRow id="dec" label="Exclude deceased" checked={spec.excludeDeceased} onChange={v => patch({ excludeDeceased: v })} />
          <ToggleRow id="contacted" label="Exclude already-contacted" checked={spec.excludeAlreadyContacted} onChange={v => patch({ excludeAlreadyContacted: v })} />
        </VStack>
      </AudienceGroup>

      <UnionOnly>
        <AudienceGroup title="Labor">
          <ToggleRow id="union" label="Union member" checked={spec.unionMemberOnly} onChange={v => patch({ unionMemberOnly: v })} />
        </AudienceGroup>
      </UnionOnly>
    </VStack>
  );
}

function ToggleRow({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <Field.Root display="flex" alignItems="center" justifyContent="space-between">
      <Field.Label htmlFor={id} mb={0} fontSize="sm">{label}</Field.Label>
      <Switch.Root id={id} checked={checked} onCheckedChange={e => onChange(e.checked === true)} >
        <Switch.HiddenInput />
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Root>
    </Field.Root>
  );
}

// Lifted verbatim from the retired IssuesStep.tsx — same patch({ issues: … }) logic and the
// abstracted "→ committed supporters of …" caption (compliance: never surface a Catalist enum value).
function directionOf(spec: GuidedListSpec, key: IssueKey): IssueDirection {
  return spec.issues.find(i => i.issue === key)?.direction ?? 'skip'
}

function IssueAlignmentRow({ spec, patch, issue }: {
  spec: GuidedListSpec
  patch: (p: Partial<GuidedListSpec>) => void
  issue: (typeof GUIDED_ISSUES)[number]
}) {
  const setDirection = (key: IssueKey, direction: IssueDirection) => {
    const others = spec.issues.filter(i => i.issue !== key)
    patch({ issues: direction === 'skip' ? others : [...others, { issue: key, direction }] })
  }
  const current = directionOf(spec, issue.key)
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Text fontWeight="semibold" fontSize="sm">{issue.label}</Text>
        <HStack gap={1}>
          <Button size="sm" variant={current === 'pro' ? 'solid' : 'outline'} colorPalette="teal"
            onClick={() => setDirection(issue.key, 'pro')}>{issue.proLabel}</Button>
          <Button size="sm" variant={current === 'opposed' ? 'solid' : 'outline'} colorPalette="orange"
            onClick={() => setDirection(issue.key, 'opposed')}>Opposed</Button>
          <Button size="sm" variant={current === 'skip' ? 'solid' : 'outline'}
            onClick={() => setDirection(issue.key, 'skip')}>Skip</Button>
        </HStack>
      </HStack>
      <Text fontSize="xs" color={current === 'skip' ? 'gray.400' : 'teal.600'} mt={2}>
        {current === 'skip' ? 'not part of this universe' : issueCompileCaption(spec.goal, current, issue.label)}
      </Text>
    </Box>
  );
}
